import type { Edge } from '@xyflow/react'
import type {
  PipelineNode,
  SourceNode,
  DestinationNode,
  TransformationNode,
  IntegrationNode,
} from '../components/canvas/types/canvas.types'
import type { AdpStore, AdpNodeRecord } from '../hooks/useAdpSessionStore'
import type { OverlayType } from '../state/overlaySlice'

export type ValidationSeverity = 'error' | 'warning'

export type ValidationRuleCode =
  | 'NODE_DISCONNECTED'
  | 'HANGING_INPUT'
  | 'HANGING_OUTPUT'
  | 'MULTIPLE_DESTINATIONS'
  | 'SOURCE_TABLE_MISSING'
  | 'DESTINATION_NOT_CONFIGURED'
  | 'DESTINATION_TABLE_MISSING'
  | 'TRANSFORMATION_INVALID'
  | 'INTEGRATION_INVALID'
  | 'INTEGRATION_INSUFFICIENT_INPUTS'
  | 'COLUMN_MAPPING_MISSING'
  | 'LOAD_TYPE_MISSING'
  | 'CYCLE_DETECTED'
  | 'NO_SOURCE'
  | 'NO_DESTINATION'
  | 'EMPTY_PIPELINE'
  | 'DUPLICATE_EDGE'

export interface ValidationFixAction {
  overlayType: OverlayType
  payload: Record<string, unknown>
}

export interface ValidationIssue {
  id: string
  code: ValidationRuleCode
  severity: ValidationSeverity
  title: string
  message: string
  scope: 'pipeline' | 'node' | 'edge'
  nodeIds: string[]
  edgeIds: string[]
  fix?: ValidationFixAction
}

export interface ValidationResult {
  isValid: boolean
  issues: ValidationIssue[]
  byNodeId: Record<string, ValidationIssue[]>
  byEdgeId: Record<string, ValidationIssue[]>
  summary: { errors: number; warnings: number; pipelineLevel: number }
}

interface ValidatorInput {
  nodes: PipelineNode[]
  edges: Edge[]
  sessionStore: AdpStore
}

type Rule = (input: ValidatorInput) => ValidationIssue[]

// ─── Helpers ──────────────────────────────────────────────────────────────────

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `vi_${Math.random().toString(36).slice(2)}_${Date.now()}`

const getNodeRecord = (store: AdpStore, nodeId: string): AdpNodeRecord | null =>
  store[nodeId] ?? null

const hasSourceConfigured = (node: SourceNode, store: AdpStore): boolean => {
  const completedSteps = Array.isArray(node.data.completed_steps)
    ? (node.data.completed_steps as string[])
    : []
  if (completedSteps.includes('configuration-details')) return true

  const record = getNodeRecord(store, node.id) as {
    sourceData?: { data?: Record<string, unknown> }
  } | null
  return Boolean(record?.sourceData?.data && Object.keys(record.sourceData.data).length > 0)
}

const getDestinationData = (
  node: DestinationNode,
  store: AdpStore
): Record<string, unknown> | null => {
  const record = getNodeRecord(store, node.id) as {
    targetData?: { data?: Record<string, unknown> }
  } | null
  return record?.targetData?.data ?? null
}

/**
 * Connection step: `DestinationConfiguration.tsx` writes
 * `data_source_instance` / `data_source_type` plus the create-target response
 * via `setTargetData`. Any of those keys is the canonical "connection done"
 * signal — keeping the check broad protects against backend response shape
 * changes.
 */
const hasDestinationConnection = (node: DestinationNode, store: AdpStore): boolean => {
  const data = getDestinationData(node, store)
  if (!data) return false
  return Boolean(
    data.data_source_instance ??
    data.data_source_type ??
    data.source_data_source_id ??
    data.target_data_source_id ??
    data.roo_id
  )
}

/**
 * Target-table step: `OutputPreview.tsx:307` writes
 * `{ outputPreview, target_table, target_columns }`. The historical
 * `target_table_id` / `target_table_name` / `createTargetResponse` keys are
 * also accepted so the rule keeps working if the backend response shape
 * changes again.
 */
const hasDestinationTable = (node: DestinationNode, store: AdpStore): boolean => {
  const data = getDestinationData(node, store)
  if (!data) return false
  if (data.target_table) return true
  if (Array.isArray(data.target_columns) && data.target_columns.length > 0) return true
  return Boolean(data.target_table_id ?? data.target_table_name ?? data.createTargetResponse)
}

/**
 * Load-type step: `LoadType.tsx:81` writes
 * `updateTargetData(nodeId, { loadTypeForm: values })`. `values.loadType`
 * comes from a zod-enum so any truthy value here is a real selection.
 */
const hasLoadType = (node: DestinationNode, store: AdpStore): boolean => {
  const data = getDestinationData(node, store)
  const loadTypeForm = data?.loadTypeForm as { loadType?: string } | undefined
  return Boolean(loadTypeForm?.loadType)
}

/**
 * File-based destinations skip the load-type step entirely. Excel takes the
 * `output-preview` shortcut from `DestinationConfiguration` and so do
 * FILESYSTEM destinations (e.g., AWS S3) — neither has CDC semantics, so
 * gating them on a load-type would block a step that doesn't exist in their
 * flow.
 */
const requiresLoadType = (node: DestinationNode, store: AdpStore): boolean => {
  const data = getDestinationData(node, store)
  if (!data) return false
  const instance =
    typeof data.data_source_instance === 'string' ? data.data_source_instance.toLowerCase() : ''
  if (instance === 'excel') return false
  const dataSourceType =
    typeof data.data_source_type === 'string' ? data.data_source_type.toUpperCase().trim() : ''
  if (dataSourceType === 'FILESYSTEM') return false
  return true
}

/**
 * Column-mapping step: `ColumnMapping.tsx` writes
 * `updateTargetData(destinationNodeId, { columnMapping: columnMappingPayload })`
 * after the backend mutations succeed. The payload is an object with array
 * fields (target_table_id, target_columns, …) so we accept both shapes.
 */
const hasColumnMapping = (node: DestinationNode, store: AdpStore): boolean => {
  const data = getDestinationData(node, store)
  const mapping = data?.columnMapping
  if (!mapping) return false
  if (Array.isArray(mapping)) return mapping.length > 0
  if (typeof mapping === 'object') return Object.keys(mapping).length > 0
  return Boolean(mapping)
}

/**
 * Transformation step: `TransformationSheet.handleSave` writes
 * `setTransformationData(nodeId, { id, name, description, abbreviation, data: appliedList })`.
 * The wrapper persists that as `transformationData.data`, so the actual
 * applied-list lives at `record.transformationData.data.data`. Without the
 * applied-list check, an empty save would falsely pass.
 */
const hasTransformationConfigured = (node: TransformationNode, store: AdpStore): boolean => {
  const record = getNodeRecord(store, node.id) as {
    transformationData?: { data?: Record<string, unknown> }
  } | null
  const stored = record?.transformationData?.data
  if (!stored || typeof stored !== 'object') return false
  const appliedList = (stored as { data?: unknown }).data
  if (Array.isArray(appliedList)) return appliedList.length > 0
  if (appliedList && typeof appliedList === 'object') {
    return Object.keys(appliedList as Record<string, unknown>).length > 0
  }
  // Fall back to the older shape (where `data` was the applied object directly).
  return Object.keys(stored).length > 0
}

/**
 * Integration step: Join/Union write
 * `setIntegrationData(nodeId, { id, name, abbreviation, data: items })` from
 * their respective sheets. Unconfigured means `integrationData` is absent or
 * its `data` array is empty — the user dragged the node but never opened the
 * sheet or saved it.
 */
const hasIntegrationConfigured = (node: IntegrationNode, store: AdpStore): boolean => {
  const record = getNodeRecord(store, node.id) as {
    integrationData?: { data?: Record<string, unknown> }
  } | null
  const stored = record?.integrationData?.data
  if (!stored || typeof stored !== 'object') return false
  const items = (stored as { data?: unknown }).data
  return Array.isArray(items) && items.length > 0
}

// ─── Rule: empty pipeline / counts ────────────────────────────────────────────

const ruleEmptyAndCounts: Rule = ({ nodes }) => {
  if (nodes.length === 0) {
    return [
      {
        id: newId(),
        code: 'EMPTY_PIPELINE',
        severity: 'error',
        title: 'Pipeline is empty',
        message:
          'Drag a Source and a Destination from the sidebar to start building your pipeline.',
        scope: 'pipeline',
        nodeIds: [],
        edgeIds: [],
      },
    ]
  }

  const issues: ValidationIssue[] = []
  const sources = nodes.filter(n => n.type === 'source')
  const destinations = nodes.filter(n => n.type === 'destination')

  if (sources.length === 0) {
    issues.push({
      id: newId(),
      code: 'NO_SOURCE',
      severity: 'error',
      title: 'No source node',
      message:
        'Every pipeline needs at least one Source. Drag a source onto the canvas to continue.',
      scope: 'pipeline',
      nodeIds: [],
      edgeIds: [],
    })
  }
  if (destinations.length === 0) {
    issues.push({
      id: newId(),
      code: 'NO_DESTINATION',
      severity: 'error',
      title: 'No destination node',
      message: 'Every pipeline needs a Destination so processed data has somewhere to land.',
      scope: 'pipeline',
      nodeIds: [],
      edgeIds: [],
    })
  }
  return issues
}

// ─── Rule: only one destination ───────────────────────────────────────────────

const ruleOneDestination: Rule = ({ nodes }) => {
  const destinations = nodes.filter(n => n.type === 'destination')
  if (destinations.length <= 1) return []
  return [
    {
      id: newId(),
      code: 'MULTIPLE_DESTINATIONS',
      severity: 'error',
      title: 'Multiple destinations not allowed',
      message: `A pipeline can have only one Destination, but found ${destinations.length}. Remove the extra ones.`,
      scope: 'pipeline',
      nodeIds: destinations.map(n => n.id),
      edgeIds: [],
    },
  ]
}

// ─── Rule: connectivity (R1 + R5) ─────────────────────────────────────────────

const ruleConnectivity: Rule = ({ nodes, edges }) => {
  if (nodes.length <= 1) return []

  const incoming = new Map<string, number>()
  const outgoing = new Map<string, number>()
  for (const node of nodes) {
    incoming.set(node.id, 0)
    outgoing.set(node.id, 0)
  }
  for (const edge of edges) {
    if (incoming.has(edge.target)) {
      incoming.set(edge.target, (incoming.get(edge.target) ?? 0) + 1)
    }
    if (outgoing.has(edge.source)) {
      outgoing.set(edge.source, (outgoing.get(edge.source) ?? 0) + 1)
    }
  }

  const issues: ValidationIssue[] = []
  for (const node of nodes) {
    const inCount = incoming.get(node.id) ?? 0
    const outCount = outgoing.get(node.id) ?? 0

    if (inCount === 0 && outCount === 0) {
      issues.push({
        id: newId(),
        code: 'NODE_DISCONNECTED',
        severity: 'error',
        title: 'Disconnected node',
        message: `"${node.data.nodeTitle}" is not connected to anything. Connect it or remove it.`,
        scope: 'node',
        nodeIds: [node.id],
        edgeIds: [],
      })
      continue
    }

    if (node.type !== 'source' && inCount === 0) {
      issues.push({
        id: newId(),
        code: 'HANGING_INPUT',
        severity: 'error',
        title: 'Missing input connection',
        message: `"${node.data.nodeTitle}" has no incoming connection. Connect an upstream node.`,
        scope: 'node',
        nodeIds: [node.id],
        edgeIds: [],
      })
    }
    if (node.type !== 'destination' && outCount === 0) {
      issues.push({
        id: newId(),
        code: 'HANGING_OUTPUT',
        severity: 'error',
        title: 'Missing output connection',
        message: `"${node.data.nodeTitle}" has no downstream connection. Connect it to the next node.`,
        scope: 'node',
        nodeIds: [node.id],
        edgeIds: [],
      })
    }
  }
  return issues
}

// ─── Rule: source has table (R3) ──────────────────────────────────────────────

const ruleSourceHasTable: Rule = ({ nodes, sessionStore }) => {
  const issues: ValidationIssue[] = []
  for (const node of nodes) {
    if (node.type !== 'source') continue
    const sourceNode = node as SourceNode
    if (hasSourceConfigured(sourceNode, sessionStore)) continue
    issues.push({
      id: newId(),
      code: 'SOURCE_TABLE_MISSING',
      severity: 'error',
      title: 'Source not configured',
      message: `Select or register a table for "${sourceNode.data.nodeTitle}" before running the pipeline.`,
      scope: 'node',
      nodeIds: [sourceNode.id],
      edgeIds: [],
      fix: {
        overlayType: 'source-picker',
        payload: {
          nodeId: sourceNode.id,
          sourceType: sourceNode.data.sourceType,
          sourceId: sourceNode.data.originalId,
        },
      },
    })
  }
  return issues
}

// ─── Rule: destination configuration stages (R4 + R7 + R8) ───────────────────
// The destination flow is sequential: connection → load type → target table
// → column mapping. Emitting only the next missing stage prevents a single
// destination from showing 4 stacked issues that would all clear together
// once the user completes the right step. Each stage's Fix routes to the
// overlay that handles that specific step.

const ruleDestinationStages: Rule = ({ nodes, sessionStore }) => {
  const issues: ValidationIssue[] = []
  for (const node of nodes) {
    if (node.type !== 'destination') continue
    const destNode = node as DestinationNode

    if (!hasDestinationConnection(destNode, sessionStore)) {
      issues.push({
        id: newId(),
        code: 'DESTINATION_NOT_CONFIGURED',
        severity: 'error',
        title: 'Destination not configured',
        message: `Configure the connection for "${destNode.data.nodeTitle}" before running.`,
        scope: 'node',
        nodeIds: [destNode.id],
        edgeIds: [],
        fix: {
          overlayType: 'destination-configuration',
          payload: {
            nodeId: destNode.id,
            sourceType: destNode.data.destinationType,
            sourceId: destNode.data.originalId,
            isOpenedDirectly: true,
          },
        },
      })
      continue
    }

    if (requiresLoadType(destNode, sessionStore) && !hasLoadType(destNode, sessionStore)) {
      issues.push({
        id: newId(),
        code: 'LOAD_TYPE_MISSING',
        severity: 'error',
        title: 'Load type not selected',
        message: `Choose a load type (Full / Incremental / CDC) for "${destNode.data.nodeTitle}".`,
        scope: 'node',
        nodeIds: [destNode.id],
        edgeIds: [],
        fix: {
          overlayType: 'load-type',
          payload: { nodeId: destNode.id },
        },
      })
      continue
    }

    if (!hasDestinationTable(destNode, sessionStore)) {
      const data = getDestinationData(destNode, sessionStore)
      issues.push({
        id: newId(),
        code: 'DESTINATION_TABLE_MISSING',
        severity: 'error',
        title: 'Destination table not selected',
        message: `Pick or create the target table for "${destNode.data.nodeTitle}" via Output Preview.`,
        scope: 'node',
        nodeIds: [destNode.id],
        edgeIds: [],
        fix: {
          overlayType: 'output-preview',
          payload: {
            nodeId: destNode.id,
            data_source_instance: (data?.data_source_instance as string | undefined) ?? null,
            data_source_type: (data?.data_source_type as string | undefined) ?? null,
          },
        },
      })
      continue
    }

    if (!hasColumnMapping(destNode, sessionStore)) {
      issues.push({
        id: newId(),
        code: 'COLUMN_MAPPING_MISSING',
        severity: 'error',
        title: 'Column mapping not saved',
        message: `Map source columns to destination columns for "${destNode.data.nodeTitle}" and save before running.`,
        scope: 'node',
        nodeIds: [destNode.id],
        edgeIds: [],
        fix: {
          overlayType: 'column-mapping',
          payload: { nodeId: destNode.id },
        },
      })
      continue
    }
  }
  return issues
}

// ─── Rule: transformation valid (R6) ──────────────────────────────────────────

const ruleTransformationValid: Rule = ({ nodes, sessionStore }) => {
  const issues: ValidationIssue[] = []
  for (const node of nodes) {
    if (node.type !== 'transformation') continue
    const tNode = node as TransformationNode
    if (hasTransformationConfigured(tNode, sessionStore)) continue
    issues.push({
      id: newId(),
      code: 'TRANSFORMATION_INVALID',
      severity: 'error',
      title: 'Transformation incomplete',
      message: `Open "${tNode.data.nodeTitle}" and complete its configuration.`,
      scope: 'node',
      nodeIds: [tNode.id],
      edgeIds: [],
      fix: {
        overlayType: 'transformation-sheet',
        payload: {
          nodeId: tNode.id,
          transformationId: tNode.data.originalId ?? null,
        },
      },
    })
  }
  return issues
}

// ─── Rule: integration valid (R7) ─────────────────────────────────────────────

/**
 * Integration nodes (Join `JR`, Union `UN`) need:
 *   - their config saved (integrationData with non-empty items)
 *   - at least 2 upstream connections — both Join and Union are meaningless
 *     with a single input. The connectivity rule (`ruleConnectivity`) only
 *     enforces `inCount >= 1`, so this catches the 1-input case specifically.
 *
 * Fix link routes to the right sheet by abbreviation (the `data.abbreviation`
 * we copy onto the node at drop time). Unknown/missing → Join, matching
 * `IntegrationNode`'s settings-click fallback.
 */
const ruleIntegrationValid: Rule = ({ nodes, edges, sessionStore }) => {
  const issues: ValidationIssue[] = []
  const inboundCountByNode = new Map<string, number>()
  for (const edge of edges) {
    inboundCountByNode.set(edge.target, (inboundCountByNode.get(edge.target) ?? 0) + 1)
  }

  for (const node of nodes) {
    if (node.type !== 'integration') continue
    const iNode = node as IntegrationNode
    const abbreviation = typeof iNode.data.abbreviation === 'string' ? iNode.data.abbreviation : ''
    const overlayType: OverlayType = abbreviation === 'UN' ? 'union-integration-sheet' : 'join-integration-sheet'
    const fix = {
      overlayType,
      payload: {
        nodeId: iNode.id,
        transformationId: iNode.data.originalId ?? null,
      },
    }

    const inboundCount = inboundCountByNode.get(iNode.id) ?? 0
    if (inboundCount < 2) {
      issues.push({
        id: newId(),
        code: 'INTEGRATION_INSUFFICIENT_INPUTS',
        severity: 'error',
        title: 'Integration needs at least two inputs',
        message: `Connect a second source upstream of "${iNode.data.nodeTitle}" to enable the ${abbreviation === 'UN' ? 'Union' : 'Join'} configuration.`,
        scope: 'node',
        nodeIds: [iNode.id],
        edgeIds: [],
        fix,
      })
      // No point checking configured-ness when it can't be configured yet.
      continue
    }

    if (hasIntegrationConfigured(iNode, sessionStore)) continue
    issues.push({
      id: newId(),
      code: 'INTEGRATION_INVALID',
      severity: 'error',
      title: 'Integration incomplete',
      message: `Open "${iNode.data.nodeTitle}" and complete its configuration.`,
      scope: 'node',
      nodeIds: [iNode.id],
      edgeIds: [],
      fix,
    })
  }
  return issues
}

// ─── Rule: cycles (R10) ───────────────────────────────────────────────────────

const ruleNoCycles: Rule = ({ nodes, edges }) => {
  const adjacency = new Map<string, string[]>()
  for (const node of nodes) adjacency.set(node.id, [])
  for (const edge of edges) {
    if (adjacency.has(edge.source)) {
      adjacency.get(edge.source)!.push(edge.target)
    }
  }

  const WHITE = 0
  const GRAY = 1
  const BLACK = 2
  const colors = new Map<string, number>(nodes.map(n => [n.id, WHITE] as const))
  const cycleNodes = new Set<string>()

  const stack: string[] = []
  const dfs = (start: string) => {
    stack.length = 0
    const path: string[] = []
    const it: { id: string; idx: number }[] = [{ id: start, idx: 0 }]
    colors.set(start, GRAY)
    path.push(start)

    while (it.length > 0) {
      const top = it[it.length - 1]
      const neighbours = adjacency.get(top.id) ?? []
      if (top.idx >= neighbours.length) {
        colors.set(top.id, BLACK)
        path.pop()
        it.pop()
        continue
      }
      const next = neighbours[top.idx++]
      const c = colors.get(next) ?? WHITE
      if (c === GRAY) {
        const cycleStart = path.indexOf(next)
        if (cycleStart >= 0) {
          for (let i = cycleStart; i < path.length; i++) {
            cycleNodes.add(path[i])
          }
          cycleNodes.add(next)
        }
      } else if (c === WHITE) {
        colors.set(next, GRAY)
        path.push(next)
        it.push({ id: next, idx: 0 })
      }
    }
  }

  for (const node of nodes) {
    if ((colors.get(node.id) ?? WHITE) === WHITE) dfs(node.id)
  }

  if (cycleNodes.size === 0) return []

  const cycleEdgeIds = edges
    .filter(e => cycleNodes.has(e.source) && cycleNodes.has(e.target))
    .map(e => e.id)

  return [
    {
      id: newId(),
      code: 'CYCLE_DETECTED',
      severity: 'error',
      title: 'Circular flow detected',
      message: 'The pipeline contains a loop. Remove the connection that creates the cycle.',
      scope: 'pipeline',
      nodeIds: Array.from(cycleNodes),
      edgeIds: cycleEdgeIds,
    },
  ]
}

// ─── Rule: duplicate edges (R12) ──────────────────────────────────────────────

const ruleDuplicateEdges: Rule = ({ edges }) => {
  const grouped = new Map<string, string[]>()
  for (const edge of edges) {
    const key = `${edge.source}::${edge.target}`
    const list = grouped.get(key) ?? []
    list.push(edge.id)
    grouped.set(key, list)
  }
  const issues: ValidationIssue[] = []
  for (const [, ids] of grouped) {
    if (ids.length <= 1) continue
    const duplicates = ids.slice(1)
    issues.push({
      id: newId(),
      code: 'DUPLICATE_EDGE',
      severity: 'error',
      title: 'Duplicate connection',
      message: 'There are multiple connections between the same two nodes. Remove the duplicates.',
      scope: 'edge',
      nodeIds: [],
      edgeIds: duplicates,
    })
  }
  return issues
}

// ─── Registry ─────────────────────────────────────────────────────────────────

const RULES: Rule[] = [
  ruleEmptyAndCounts,
  ruleOneDestination,
  ruleConnectivity,
  ruleSourceHasTable,
  ruleDestinationStages,
  ruleTransformationValid,
  ruleIntegrationValid,
  ruleNoCycles,
  ruleDuplicateEdges,
]

// ─── Public API ───────────────────────────────────────────────────────────────

export function validatePipeline(input: ValidatorInput): ValidationResult {
  const issues: ValidationIssue[] = []
  for (const rule of RULES) {
    issues.push(...rule(input))
  }

  const byNodeId: Record<string, ValidationIssue[]> = {}
  const byEdgeId: Record<string, ValidationIssue[]> = {}
  let errors = 0
  let warnings = 0
  let pipelineLevel = 0

  for (const issue of issues) {
    if (issue.severity === 'error') errors++
    else warnings++
    if (issue.scope === 'pipeline') pipelineLevel++

    for (const nodeId of issue.nodeIds) {
      ;(byNodeId[nodeId] ??= []).push(issue)
    }
    for (const edgeId of issue.edgeIds) {
      ;(byEdgeId[edgeId] ??= []).push(issue)
    }
  }

  return {
    isValid: errors === 0,
    issues,
    byNodeId,
    byEdgeId,
    summary: { errors, warnings, pipelineLevel },
  }
}
